import { useId, useRef } from 'react';
import type { OrderablePackage } from '@mypoz/shared';
import { MAX_RECEIPTS_PER_PACKAGE, receiptImageUrl } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';
import { legacyAssetOrigin } from '@/lib/legacy-api.js';
import type { ReceiptSlot } from '@/features/orders/useReceiptUploads.js';

export interface PackageRowProps {
  item: OrderablePackage;
  selected: boolean;
  /** False when the selection is already at the per-order limit. */
  selectable: boolean;
  /** This package's receipts (MYP-96). */
  receipts?: readonly ReceiptSlot[];
  onToggle: (packageId: string) => void;
  /** Picks and uploads a receipt. Omitted when receipts cannot be attached here. */
  onAttach?: (packageId: string, file: File) => void;
  /**
   * Draws the upload button locked, with a note, instead of leaving it out — the edit flow, whose
   * endpoint (`order_modify`) takes no receipt, so one picked there could never reach the order.
   */
  receiptsLocked?: boolean;
  onRetry?: (packageId: string, slotId: string) => void;
  onRemove?: (packageId: string, slotId: string) => void;
  /**
   * Whether this package needs a receipt on this order (MYP-140). When it does not — a China or
   * Malaysia warehouse without insurance — the row has no receipt control: the vendor would throw
   * one away.
   */
  wantsReceipt?: boolean;
  /** Receipts the order already has for this package (Edit order), shown read-only. */
  savedReceipts?: readonly string[];
}

/** The 44px square every receipt control is drawn in (phone order layout). */
const TILE = 'relative flex size-11 shrink-0 items-center justify-center rounded-md';
const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

/**
 * One package in the picker: tick it to put it on the order, and attach its receipt.
 *
 * The receipt control appears only on a selected row that needs a receipt (MYP-140) — a package
 * that is not on this order has nothing to declare, a China or Malaysia one without insurance has
 * a receipt the vendor would throw away, and upload buttons on either invite receipts that go
 * nowhere.
 *
 * phone order layout: the receipts are a strip of 44px tiles rather than full-width buttons, so a ticked
 * package grows by one tile row at most and the next package stays in view. With no receipt yet,
 * the row ends in a small upload button and says "Receipt needed"; with some, each shows as a
 * thumbnail (tap to open it) with a × to remove it, and a dashed + adds another, up to three. The
 * rules are unchanged: what holds Step 1 is still decided by `CreateOrder`.
 */
export function PackageRow({
  item,
  selected,
  selectable,
  receipts = [],
  onToggle,
  onAttach,
  onRetry,
  onRemove,
  receiptsLocked = false,
  wantsReceipt = true,
  savedReceipts = [],
}: PackageRowProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const fileInput = useRef<HTMLInputElement>(null);
  const checkboxId = useId();

  const detail =
    item.weightGrams === undefined
      ? t('orders.newOrder.awaitingIntake')
      : t('orders.newOrder.weightKg', { value: fmt.number(item.weightGrams / 1000, 1) });

  const attachable = selected && wantsReceipt && onAttach !== undefined;
  // Edit order: the receipts already on the order, and — only where one is needed but none is
  // there — the locked button with its note. `order_modify` takes no receipt (MYP-96).
  const saved = selected && !onAttach ? savedReceipts : [];
  const lockedHere = selected && !onAttach && receiptsLocked && wantsReceipt && saved.length === 0;
  // A refused pick (wrong type, too large) is replaced by the next one, so it doesn't count.
  const kept = receipts.filter((slot) => slot.status !== 'failed' || slot.problem === 'failed');
  const canAddMore = kept.length < MAX_RECEIPTS_PER_PACKAGE;
  // Only a hint on the row: whether Step 1 is held is still `CreateOrder`'s call (MYP-96).
  const needsReceipt =
    attachable &&
    !receipts.some((slot) => slot.status === 'uploaded' || slot.status === 'uploading');
  const failures = receipts.filter((slot) => slot.status === 'failed');
  const pick = () => fileInput.current?.click();

  return (
    // MYP-131: the tick and the package share a line on a phone too. phone order layout: the receipt strip
    // wraps under the package's text there, and sits at the end of the line from `sm`.
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3 sm:flex-nowrap sm:items-center sm:px-6 lg:py-2">
      <input
        id={checkboxId}
        type="checkbox"
        checked={selected}
        disabled={!selected && !selectable}
        onChange={() => onToggle(item.id)}
        className="mt-0.5 size-5 shrink-0 accent-brand disabled:opacity-40 sm:mt-0"
      />

      <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
        <span className="block truncate text-sm font-semibold text-ink">{item.trackingNumber}</span>
        <span className="mt-0.5 block text-sm text-muted">
          {/* Rendered as the customer typed it — often bilingual — in every language. */}
          {item.itemName} × {fmt.number(item.quantity)} · {detail}
        </span>
        {needsReceipt ? (
          <span className="mt-1 flex items-center gap-1 text-xs font-semibold text-danger">
            <Icon name="error" size={14} />
            {t('orders.newOrder.receipt.needed')}
          </span>
        ) : null}
        {lockedHere ? (
          <span className="mt-1 block text-xs text-muted">
            {t('orders.newOrder.receipt.lockedOnEdit')}
          </span>
        ) : null}
      </label>

      {saved.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 pl-9 sm:pl-0">
          {saved.map((path, index) => {
            const url = receiptImageUrl(path, legacyAssetOrigin);
            const name = t('orders.detail.receiptLink', { number: index + 1 });
            return (
              <a
                key={path}
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-label={t('orders.newOrder.receipt.viewLabel', { name })}
                title={name}
                // Quoted, and the URL is encoded, so no character in a vendor path can end it.
                style={{ backgroundImage: `url("${url}")` }}
                className={cn(TILE, 'border border-line bg-surface-alt bg-cover bg-center', FOCUS)}
              />
            );
          })}
        </div>
      ) : null}

      {attachable && receipts.length === 0 ? (
        <button
          type="button"
          onClick={pick}
          aria-label={t('orders.newOrder.receipt.uploadFor', { tracking: item.trackingNumber })}
          title={t('orders.newOrder.uploadReceipt')}
          className={cn(
            'inline-flex size-11 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-brand/50',
            'text-sm font-semibold text-brand transition-colors hover:bg-brand-soft',
            // From `sm` it says what it does too; a laptop row stays one slim line.
            'sm:w-auto sm:px-3 lg:h-8',
            FOCUS,
          )}
        >
          <Icon name="upload_file" size={20} />
          <span className="hidden sm:inline">{t('orders.newOrder.uploadReceipt')}</span>
        </button>
      ) : null}

      {attachable && receipts.length > 0 ? (
        <div className="flex w-full flex-col gap-1.5 pl-9 sm:w-auto sm:items-end sm:pl-0">
          {/* gap-4 and pt-2.5: room for each tile's remove dot (see `ReceiptTile`). */}
          <div className="flex flex-wrap items-center gap-4 pt-2.5">
            {receipts.map((slot) => (
              <ReceiptTile
                key={slot.id}
                slot={slot}
                onRetry={onRetry ? () => onRetry(item.id, slot.id) : undefined}
                onRemove={onRemove ? () => onRemove(item.id, slot.id) : undefined}
              />
            ))}
            {canAddMore ? (
              <button
                type="button"
                onClick={pick}
                aria-label={t('orders.newOrder.receipt.addAnotherFor', {
                  tracking: item.trackingNumber,
                })}
                title={t('orders.newOrder.receipt.addAnother')}
                className={cn(
                  TILE,
                  'border-[1.5px] border-dashed border-brand bg-surface text-brand transition-colors hover:bg-brand-soft',
                  FOCUS,
                )}
              >
                <Icon name="add" size={20} />
              </button>
            ) : null}
          </div>
          {failures.map((slot) => (
            <p key={slot.id} role="alert" className="text-xs text-danger sm:text-right">
              {t('orders.newOrder.receipt.problemName', {
                name: slot.name,
                problem: t(`orders.newOrder.receipt.problem.${slot.problem ?? 'failed'}`),
              })}
            </p>
          ))}
        </div>
      ) : null}

      {attachable ? (
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png"
          aria-label={t('orders.newOrder.receiptFor', { tracking: item.trackingNumber })}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onAttach(item.id, file);
            // Cleared so picking the same file twice still fires a change event.
            event.target.value = '';
          }}
        />
      ) : null}

      {lockedHere ? (
        <button
          type="button"
          disabled
          aria-label={t('orders.newOrder.receipt.uploadFor', { tracking: item.trackingNumber })}
          className={cn(
            'inline-flex size-11 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-line',
            'cursor-not-allowed text-sm font-semibold text-muted opacity-60 sm:w-auto sm:px-3 lg:h-8',
          )}
        >
          <Icon name="upload_file" size={20} />
          <span className="hidden sm:inline">{t('orders.newOrder.uploadReceipt')}</span>
        </button>
      ) : null}
    </div>
  );
}

/**
 * One receipt as a 44px tile: the thumbnail once uploaded (a link that opens the full image), a
 * spinner while uploading, or a red tile when it failed — tap to retry where it can be. Every
 * tile but an uploading one has a small × to remove it; its hit area is grown to 44px.
 */
function ReceiptTile({
  slot,
  onRetry,
  onRemove,
}: {
  slot: ReceiptSlot;
  onRetry?: () => void;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();

  /*
    An uploaded slot always carries its path today (`useReceiptUploads` sets both together). Should
    one ever arrive without it, it is drawn as still uploading — never as a failure — so the tile
    agrees with the row, which counts an uploaded slot as its receipt (review of #46).
  */
  const busy = slot.status === 'uploading' || (slot.status === 'uploaded' && !slot.path);

  let tile;
  if (slot.status === 'uploaded' && slot.path) {
    const url = receiptImageUrl(slot.path, legacyAssetOrigin);
    tile = (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        aria-label={t('orders.newOrder.receipt.viewLabel', { name: slot.name })}
        title={slot.name}
        // Quoted, and the URL is encoded, so no character in a vendor path can end it.
        style={{ backgroundImage: `url("${url}")` }}
        className={cn(TILE, 'border border-line bg-surface-alt bg-cover bg-center', FOCUS)}
      />
    );
  } else if (busy) {
    tile = (
      <span role="status" className={cn(TILE, 'border border-line bg-surface-alt text-brand')}>
        <Icon name="progress_activity" size={20} className="animate-spin" />
        <span className="sr-only">
          {t('orders.newOrder.receipt.uploadingName', { name: slot.name })}
        </span>
      </span>
    );
  } else if (slot.problem === 'failed' && onRetry) {
    tile = (
      <button
        type="button"
        onClick={onRetry}
        aria-label={t('orders.newOrder.receipt.retryLabel', { name: slot.name })}
        title={t('orders.newOrder.receipt.retry')}
        className={cn(
          TILE,
          'border-[1.5px] border-danger bg-surface text-danger hover:bg-surface-alt',
          FOCUS,
        )}
      >
        <Icon name="refresh" size={20} />
      </button>
    );
  } else {
    tile = (
      <span
        title={slot.name}
        className={cn(TILE, 'border-[1.5px] border-danger bg-surface text-danger')}
      >
        <Icon name="error" size={20} />
      </span>
    );
  }

  return (
    <span className="relative inline-flex">
      {tile}
      {!busy && onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('orders.newOrder.receipt.removeLabel', { name: slot.name })}
          className={cn(
            'absolute -top-2.5 -right-2.5 flex size-5 items-center justify-center rounded-full bg-ink text-surface',
            /*
              The dot sits half outside its tile, and its touch area grows only 6px (32px in all),
              so it covers the tile's corner but not the tile, and the 16px gap keeps it off the
              next tile: a tap meant to open a receipt never removes it (review of #46).
            */
            'after:absolute after:-inset-1.5',
            FOCUS,
          )}
        >
          <Icon name="close" size={12} />
        </button>
      ) : null}
    </span>
  );
}
